import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Alert } from 'src/app/interfaces/alert';
import {
  RegistrationPaymentInfo,
  User,
} from 'src/app/interfaces/user';
import { LogService } from 'src/app/services/common/log.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { PaymentConfigService } from 'src/app/services/payment-config/payment-config.service';
import { RegistrationFlowStep } from 'src/app/constants/registration-flow.constants';
import { VET_GROUP_V4P } from 'src/app/constants/payment-config.constants';
import { RegistrationProgressData } from 'src/app/services/registration-progress/registration-progress.service';
import { PricingTableSubTypes } from 'src/app/shared/components/pricing-table/pricing-table.component';
import { environment } from 'src/environments/environment';
import { PlanFeatureService } from '../../../services/common/plan-feature.service';
import { PetService } from 'src/app/services/pet/pet.service';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { ConfigService } from '../../../services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-membership-options',
  templateUrl: './membership-options.component.html',
  styleUrls: ['./membership-options.component.scss'],
})
export class MembershipOptionsComponent implements OnInit {
  public alert: Alert = null;
  public loading: boolean;
  public pricingPlans = environment.stripePlans;
  public user: User;
  public currentMembershipName: string = '';
  public userAlreadyHasASubscription: boolean;
  public petName: string;
  public userCurrentProductType: number;
  public showBasicAccountButton: boolean = true;
  public showContinueWithBasicOnTop: boolean;
  public showVetGroupMessage: boolean = false;
  public isIdentiSmartTagPlanAvailable: boolean;
  // Hidden once the step is completed, for example for keepers adding a pet after registration
  public showProgressStepper = false;
  private isBasicMembershipOnlyAvailableOnTop: boolean = false;

  private isBrowser: boolean;

  // Registration payment config (/api/registration/me/payment)
  private isRegistrationPaymentConfigEnabled: boolean = false;
  public availableProductIds: number[] | null = null;

  public plan1Route: string;
  public plan2Route: string;
  public membershipSelectionTitle: string;
  public membershipSelectionVet4PetTitle: string;
  public membershipSelectionSubTitle: string;
  public membershipSelectionSubTitleButtonText: string;

  constructor(
    private _logService: LogService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private configService: ConfigService,
    public membershipOptionsService: MemberShipOptionsService,
    private _gtmService: GoogleTagManagerService,
    public planFeatureService: PlanFeatureService,
    private featureFlagService: FeatureFlagsService,
    private registrationFlowService: RegistrationFlowService,
    private paymentConfigService: PaymentConfigService,
    private petService: PetService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.user = this._activatedRoute.snapshot.data.user as User;
    this.membershipSelectionTitle = this.configService.getTextFromKey('membership-selection-title');
    this.membershipSelectionVet4PetTitle = this.configService.getTextFromKey('membership-selection-vet-4-pets-title');
    this.membershipSelectionSubTitle = this.configService.getTextFromKey('membership-selection-sub-title');
    this.membershipSelectionSubTitleButtonText = this.configService.getTextFromKey('membership-selection-sub-title-button-text');
    this.plan2Route = this.configService.getTextFromKey('2-plan-route');
    this.plan1Route = this.configService.getTextFromKey('1-plan-route');
    this.isIdentiSmartTagPlanAvailable = this.featureFlagService.isEnabled('isIdentiSmartTagPlanAvailable');

    this.isRegistrationPaymentConfigEnabled = this.featureFlagService.isEnabled('isRegistrationPaymentConfigEnabled');
    this.isBasicMembershipOnlyAvailableOnTop = this.featureFlagService.isEnabled('isBasicMembershipOnlyAvailableOnTop');

    this.userAlreadyHasASubscription = this.membershipOptionsService.doesUserHaveMembership(
      this.user
    );

    if (this.userAlreadyHasASubscription) {
      this.currentMembershipName = this.membershipOptionsService.getUserMembershipName(this.user);
    }
  }

  ngOnInit(): void {
    if (this.user) {
      this.userCurrentProductType = this.user.productType;
      this.petName = this.petService.createPetsListString(this.user.pets);
    }

    this.membershipSelectionSubTitle = this.membershipSelectionSubTitle?.replace(
      '{{petName}}',
      this.petName || 'your pet',
    );

    // Payment config (cards + skip button), pre-warmed by the resolver and read from cache.
    if (this.isBrowser && this.isRegistrationPaymentConfigEnabled) {
      const paymentConfig = this.paymentConfigService.config;
      if (paymentConfig) {
        this.applyRegistrationPaymentConfig(paymentConfig);
      }
    }

    // Warm the steps cache so getNextStepRoute() can resolve the next step.
    this.registrationFlowService.loadSteps().subscribe(() => {
      this.showProgressStepper = !this.registrationFlowService.isStepCompleted(RegistrationFlowStep.MembershipOptions);
    });
  }

  public getProgressData() {
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: true,
      isPetChipNumberRegistrationCompleted: true,
      isMembershipSelectionCompleted: false,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }

  public select(planId?: string) {
    switch (planId) {
      case environment.stripePlans.identibasePlus:
        this._router.navigate([this.plan1Route]);
        break;
      case environment.stripePlans.identiAlertMonthly:
        this._router.navigate([this.plan2Route]);
        break;
      case environment.stripePlans.identiAlertAnnual:
        this._router.navigate([this.plan2Route]);
        break;
      default:
        this._router.navigate([this.plan2Route]);
    }
  }

  public navigateToProductPage(pricingTableSubType: string): void {
    switch (pricingTableSubType) {
      case PricingTableSubTypes.IDENTIBASEPLUS:
        this._router.navigate([this.plan1Route]);
        break;
      case PricingTableSubTypes.IDENTIALERT:
        this._router.navigate([this.plan2Route]);
        break;
    }
  }

  private pushGTMTag(user: User): void {
    if (!environment.googleTagManagerId || !environment.conversionSendToParam) return;
    
    const productName = this.membershipOptionsService.getUserMembershipAnalyticsName(user);

    this._gtmService.pushTag({
      event: 'conversion',
      send_to: environment.conversionSendToParam,
      product_name: productName,
      environment: environment.name
    }).catch(() => {});
  }

  /**
   * Applies the /api/registration/me/payment response.
   * isPaymentSkippable decides whether the keep basic account button shows, and
   * availableProductIds is handed to the pricing table to override card availability.
   */
  private applyRegistrationPaymentConfig(info: RegistrationPaymentInfo): void {
    this.showBasicAccountButton = info.isPaymentSkippable;
    this.showVetGroupMessage = info.context?.vetGroup === VET_GROUP_V4P;
    this.availableProductIds = this.paymentConfigService.getAvailableProductIds();

    // Show the basic membership on top
    if (this.isBasicMembershipOnlyAvailableOnTop) {
      this.showContinueWithBasicOnTop = true;
    }
  }

  private getNextStepRoute(): string {
    return this.registrationFlowService.getNextRoute(RegistrationFlowStep.MembershipOptions) ?? '/welcome-gift';
  }

  public continueWithCurrentMembership() {
    // Ignores repeated clicks while the step is being completed; the page is left afterwards
    if (this.loading) {
      return;
    }
    this.completeMembershipStep().subscribe(() => this._router.navigate([this.getNextStepRoute()]));
  }

  public completeMembershipSelection() {
    if (!this.loading) {
      // Keepers back here after registration, for example adding a pet, were already counted as a
      // conversion
      const isFirstSelection = !this.registrationFlowService.isStepCompleted(RegistrationFlowStep.MembershipOptions);
      this.completeMembershipStep()
        .subscribe(
          () => {
            this.alert = {
              type: 'success',
              message:
                "Your account information was updated. You'll get redirected in a few seconds!",
            };
            if (isFirstSelection) {
              this.pushGTMTag(this.user);
            }
            setTimeout(() => {
              this._router.navigate([this.getNextStepRoute()]);
            }, 1000);
            return;
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
            this._logService.error(
              'An error has occurred during membership selection save. Please try again later. ',
              err
            );
          }
        )
        .add(() => {
          this.loading = false;
        });
    }
  }

  public closeAlert() {
    this.alert = null;
  }

  // Completes the step on the backend, unless it already is, for example for keepers adding a pet.
  // Paid plans don't come through here: the backend completes the step after a successful payment.
  private completeMembershipStep(): Observable<void> {
    this.closeAlert();
    this.loading = true;
    return this.registrationFlowService.completeStep(RegistrationFlowStep.MembershipOptions);
  }
}
